// Branding for the client portal pages: the business's logo, name and address
// in the header, and on every page a footer with
// "(c) <year> <Business>. All rights reserved." and "Powered by eloNest".
// Without a known business the pages show eloNest only.
import React, { useEffect, useState } from 'react';
import portalAPI from './portalApi';
import { assetUrl } from '../../utils/assetUrl';

/** { brand, missing } for a slug. brand is null until loaded, or when there is no slug. */
export function usePortalBrand(slug) {
  const [state, setState] = useState({ brand: null, missing: false });
  useEffect(() => {
    let live = true;
    setState({ brand: null, missing: false });
    if (!slug) return undefined;
    portalAPI.brand(slug)
      .then((r) => { if (live) setState({ brand: r.data, missing: false }); })
      .catch(() => { if (live) setState({ brand: null, missing: true }); });
    return () => { live = false; };
  }, [slug]);
  return state;
}

export function PortalHeader({ brand, right = null }) {
  const [logoOk, setLogoOk] = useState(true);
  if (!brand) return null;
  const logo = logoOk ? assetUrl(brand.logo_url) : null;
  return (
    <header className="flex flex-wrap items-center justify-between gap-3 border-b pb-3 mb-4" data-testid="portal-brand-header">
      <div className="flex items-center gap-3 min-w-0">
        {logo && <img src={logo} alt={`${brand.name} logo`} className="h-12 w-auto max-w-[160px] object-contain" onError={() => setLogoOk(false)} />}
        <div className="min-w-0">
          <p className="text-lg font-semibold break-words">{brand.name}</p>
          {brand.address && <p className="text-sm text-gray-600 whitespace-pre-line break-words" data-testid="portal-brand-address">{brand.address}</p>}
        </div>
      </div>
      {right}
    </header>
  );
}

export function PortalFooter({ brand }) {
  const year = new Date().getFullYear();
  const name = brand && brand.name ? brand.name : 'eloNest';
  return (
    <footer className="mt-10 border-t pt-4 pb-6 text-center text-xs text-gray-600 space-y-1" data-testid="portal-footer">
      <p>&copy; {year} {name}. All rights reserved.</p>
      <a href="https://elonest.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
        <img src="/img/eloNestLogo.png" alt="eloNest logo" className="h-4 w-auto" />
        <span>Powered by eloNest</span>
      </a>
    </footer>
  );
}

/** A full portal page: branded header (when a business is known), content and footer. */
export function PortalShell({ brand, right = null, children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 w-full max-w-3xl mx-auto px-4 pt-6">
        {brand ? <PortalHeader brand={brand} right={right} />
          : right && <div className="flex justify-end mb-4">{right}</div>}
        {children}
      </div>
      <PortalFooter brand={brand} />
    </div>
  );
}
